import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import {
  useBaseAsync,
  type HpsfirProTableColumn,
  type HpsfirProTableData,
  type HpsfirProTableFilter,
  type HpsfirProTableGetDataFunction,
  type HpsfirProTableInstance,
  type HpsfirProFormInstance,
} from 'hpsfir-managebase-ui';

import { useApi } from '@/hooks/useApi';
import { ElMessage } from 'element-plus';
import { ref, unref } from 'vue';
import { normalizeBizTableColumns } from '@/utils/tableColumns';
import dayjs from 'dayjs';
import BusinessDangerDialog from '@/components/BusinessDangerDialog.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const columns = ref<HpsfirProTableColumn[]>(normalizeBizTableColumns([]));
const tableRef = ref<HpsfirProTableInstance>();
const setModFormRef = ref<HpsfirProFormInstance>();
const updateDialogRef = ref<any>();
const recoveryDialogRef = ref<any>();
const currentRow = ref<any>();

const modMap: any = {
  open: '打开',
  close: '关闭',
  control: '点动',
};

const setModItems: HpsfirProTableColumn[] = [
  {
    name: 'mod',
    label: '设备模式',
    type: 'searchSelect',
    options: [
      {
        label: '常开',
        value: 'open',
      },
      {
        label: '常闭',
        value: 'close',
      },
      {
        label: '点动',
        value: 'control',
      },
    ],
    isCondition: true,
    rule: [{ required: true }],
  },
];

const formatDeviceMod = (mod: any) => {
  if (Array.isArray(mod)) {
    return `${modMap[mod[0] as any]} (${modMap[mod[1] as any]})`;
  }
  return modMap[mod as any] || '-';
};

const init = useBaseAsync(async() => {
  const deviceTypes = await useApi().deviceApi.listDeviceTypeDevice();
  const options
    = deviceTypes.data.data?.map((item: any) => ({
      value: item.id,
      label: item.name,
    })) || [];

  columns.value = normalizeBizTableColumns([
    {
      name: 'remark',
      label: '设备备注',
      type: 'string',
      isCondition: true,
      order: 10,
    },
    {
      name: 'idHex',
      label: '设备序列号',
      type: 'string',
      hideCreate: true,
      hideEdit: true,
      isCondition: true,
    },
    {
      name: 'deviceTypeId',
      label: '设备类型',
      type: 'searchSelect',
      options,
      rule: [{ required: true }],
      isCondition: true,
      render: (scope: any) => {
        const deviceType = options.find((item: any) => item.value === scope?.row?.deviceTypeId);
        return deviceType?.label || '-';
      },
    },
    {
      name: 'serialNumber',
      label: '原始序列号',
      type: 'string',
      isCondition: true,
      rule: [{ required: true }],
    },
    {
      name: 'status',
      label: '设备状态',
      type: 'string',
      hideCreate: true,
      hideEdit: true,
    },
    {
      name: 'mod',
      label: '设备模式 (期望值)',
      type: 'string',
      hideCreate: true,
      hideEdit: true,
    },
    {
      name: 'version',
      label: '设备版本',
      type: 'string',
      hideCreate: true,
      hideEdit: true,
    },
    {
      name: 'createTime',
      label: '创建时间',
      type: 'string',
      hideCreate: true,
      hideEdit: true,
    },
  ]);
});

const loading = ref(false);

const mapExportDeviceRow = (item: any) => ({
  ...item,
  status: item.status ?? '',
  version: item.version ?? '',
  mod: item.mod ? formatDeviceMod(item.mod) : '',
  createTime: item.createTime ? dayjs(item.createTime).format('YYYY-MM-DD HH:mm:ss') : '',
});

const mapDeviceRow = (item: any, exporting?: boolean) => {
  if (exporting) {
    return mapExportDeviceRow(item);
  }

  const statusRef = ref('加载中...');
  const versionRef = ref('加载中...');
  const modRef = ref('加载中...');
  const modValueRef = ref();

  useApi().deviceApi.deviceStatusDevice(item.id, {
    headers: {
      'custom-error': 'true',
    },
  }).then((statusRes) => {
    if (typeof statusRes.data.data === 'boolean') {
      statusRef.value = statusRes.data.data ? '在线' : '离线';
    } else {
      statusRef.value = statusRes.data.data || '-';
    }
  })
    .catch(() => {
      statusRef.value = '-';
    });
  useApi().deviceApi.deviceVersionDevice(item.id, {
    headers: {
      'custom-error': 'true',
    },
  }).then((versionRes) => {
    versionRef.value = versionRes.data.data || '-';
  })
    .catch(() => {
      versionRef.value = '-';
    });
  useApi().deviceApi.deviceGetModDevice(item.id, {
    headers: {
      'custom-error': 'true',
    },
  }).then((modRes) => {
    modValueRef.value = Array.isArray(modRes.data.data) ? modRes.data.data[0] : modRes.data.data;
    modRef.value = formatDeviceMod(modRes.data.data);
  })
    .catch(() => {
      modRef.value = '-';
    });

  return {
    ...item,
    createTime: item.createTime ? dayjs(item.createTime).format('YYYY-MM-DD HH:mm:ss') : '',
    status: statusRef,
    version: versionRef,
    mod: modRef,
    modValue: modValueRef,
  };
};

const getData = useBaseAsync<HpsfirProTableGetDataFunction>(async(filter: HpsfirProTableFilter) => {
  try {
    loading.value = true;
    console.log(filter);
    const res = await useApi().deviceApi.pageDeviceDevice({
      pageQuery: {
        page: filter.paginations.page - 1,
        pageSize: filter.paginations.pageSize,
      },
      orderBys: filter.orderBys,
      conditions: {
        remark: filter.conditions.remark || undefined,
        idHex: filter.conditions.idHex || undefined,
        deviceTypeId: filter.conditions.deviceTypeId || undefined,
        serialNumber: filter.conditions.serialNumber || undefined,
      },
    });
    const tableData: HpsfirProTableData = {
      total: res.data.data?.total || 0,
      data: res.data.data?.data?.map((item: any) => mapDeviceRow(item, filter.exporting)) || [],
    };
    return tableData;
  } finally {
    loading.value = false;
  }
});

const formatDeleteInfo = (row: any) => `您正在进行删除(${row.idHex})操作，请谨慎操作`;
const formatUpdateInfo = (row: any) => `您正在进行更新(${row?.idHex})操作，请保证设备通电稳定在线，下发更新后，请等待60秒刷新查看版本`;
const formatRecoveryInfo = (row: any) => `您正在进行恢复(${row?.idHex})操作，请保证设备通电稳定在线，下发恢复后，请等待60秒刷新查看版本`;
const getRowValue = (value: any) => unref(value);

const addFun = useBaseAsync(async(data: any) => {
  await useApi().deviceApi.addDeviceDevice({
    deviceAdminVo: data,
  });
  ElMessage.success('添加成功');
});

const updateFun = useBaseAsync(async(data: any) => {
  await useApi().deviceApi.updateDeviceDevice({
    deviceAdminVo: data,
  });
  ElMessage.success('修改成功');
});
const deleteFun = useBaseAsync(async(data: any) => {
  await useApi().deviceApi.deleteDeviceDevice(data.id);
  ElMessage.success('删除成功');
});

const handleUpdateAction = useBaseAsync(async(data: any) => {
  await useApi()
    .deviceApi.deviceUpgradeDevice(data.id, {
      headers: {
        'custom-error': 'true',
      },
    })
    .then(() => {
      ElMessage.success('下发更新成功, 请等60秒后刷新列表');
    })
    .catch(() => {
      ElMessage.error('下发更新失败');
    });
});

const handleRecoveryAction = useBaseAsync(async(data: any) => {
  await useApi()
    .deviceApi.deviceRecoveryDevice(data.id, {
      headers: {
        'custom-error': 'true',
      },
    })
    .then(() => {
      ElMessage.success('下发恢复成功, 请等60秒后刷新列表');
    })
    .catch(() => {
      ElMessage.error('下发恢复失败');
    });
});

const operates = [
  {
    key: 'opendoor',
    name: '远程开门',
    handler: useBaseAsync(async(row: any) => {
      await useApi().deviceApi.deviceAdminOpendoorDevice(row.id);
      ElMessage.success('成功');
      tableRef.value?.refresh();
    }),
  },
  {
    key: 'setMod',
    name: '设置模式',
    handler: useBaseAsync((row: any) => {
      currentRow.value = row;
      setModFormRef.value?.setOneFormState('mod', getRowValue(row.modValue) || getRowValue(row.mod));
      setModFormRef.value?.showDialog();
    }),
  },
  {
    key: 'deviceUpdate',
    name: '更新版本',
    handler: (row: any) => {
      updateDialogRef.value?.showDialog(row);
    },
  },
  {
    key: 'deviceRecovery',
    name: '恢复版本',
    handler: (row: any) => {
      recoveryDialogRef.value?.showDialog(row);
    },
  },
];

const handleSetMod = useBaseAsync(async(data: any) => {
  if (!currentRow.value.id) {
    ElMessage.error('请选择一个设备');
    return;
  }
  try {
    await useApi().deviceApi.deviceSetModDevice(currentRow.value.id, data.mod);
    ElMessage.success('设置模式成功');
  } catch (error) {
    if ((error as any)?.response?.status !== 501) {
      throw error;
    }
  }
  tableRef.value?.refresh();
});

init();


return (_ctx: any,_cache: any) => {
  const _component_HpsfirProPageHeader = _resolveComponent("HpsfirProPageHeader")!
  const _component_HpsfirProTable = _resolveComponent("HpsfirProTable")!
  const _component_HpsfirProForm = _resolveComponent("HpsfirProForm")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_HpsfirProPageHeader, {
      title: "设备库存",
      "title-en": "DEVICE",
      description: "管理门禁设备库存、设备类型、运行状态和设备模式"
    }),
    _createVNode(_component_HpsfirProTable, {
      ref_key: "tableRef",
      ref: tableRef,
      "export-file-name": "设备库存",
      paginations: { pageSize: 10 },
      columns: columns.value,
      "get-data": _unref(getData),
      "on-add": _unref(addFun),
      "on-delete": _unref(deleteFun),
      "on-edit": _unref(updateFun),
      loading: loading.value,
      "is-dangder-delete": true,
      "format-dangder-delete-info": formatDeleteInfo,
      operates: operates
    }, null, 8, ["columns", "get-data", "on-add", "on-delete", "on-edit", "loading"]),
    _createVNode(_component_HpsfirProForm, {
      ref_key: "setModFormRef",
      ref: setModFormRef,
      title: "设置模式",
      "is-dialog": true,
      "dialog-type": "drawer",
      items: setModItems,
      "on-ok": _unref(handleSetMod)
    }, null, 8, ["on-ok"]),
    _createVNode(BusinessDangerDialog, {
      ref_key: "updateDialogRef",
      ref: updateDialogRef,
      title: "更新版本",
      "opt-name": "更新",
      "on-ok": _unref(handleUpdateAction),
      "format-dangder-delete-info": formatUpdateInfo
    }, null, 8, ["on-ok"]),
    _createVNode(BusinessDangerDialog, {
      ref_key: "recoveryDialogRef",
      ref: recoveryDialogRef,
      title: "恢复版本",
      "opt-name": "恢复",
      "on-ok": _unref(handleRecoveryAction),
      "format-dangder-delete-info": formatRecoveryInfo
    }, null, 8, ["on-ok"])
  ], 64))
}
}

})